import { Controller } from "@hotwired/stimulus";
import { t } from "../../lib/i18n";

const INDICATOR_STATES = {
  unsaved: { i18n: "views.hiring.job_questions.v2.autosave.unsaved", fallback: "Unsaved changes…" },
  saved:   { i18n: "views.hiring.job_questions.v2.autosave.saved",   fallback: "Draft saved", prefix: "● " },
  error:   { i18n: "views.hiring.job_questions.v2.autosave.error",   fallback: "Could not save draft." },
  idle:    { text: "" },
};

export default class extends Controller {
  static targets = ["textarea", "indicator", "lockVersion", "categorySelect"];
  static values = {
    url: String,
    lockVersion: Number,
    debounce: { type: Number, default: 1500 },
  };

  connect() {
    this.timeout = null;
    this.dirty = false;
  }

  disconnect() {
    clearTimeout(this.timeout);
  }

  scheduleSave() {
    this.dirty = true;
    this.setIndicator("unsaved");
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.save(), this.debounceValue);
  }

  async save() {
    if (!this.dirty) return;
    this.dirty = false;

    const token = this.csrfToken();
    const payload = {
      lock_version: this.lockVersionValue,
      draft_response: this.textareaTarget.value,
      authenticity_token: token,
    };
    if (this.hasCategorySelectTarget) {
      payload.job_question_category_id = this.categorySelectTarget.value;
    }

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": token,
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 409) {
        const body = await response.json();
        this.setIndicator("idle");
        // dispatch() namespaces the event with our identifier; conflict_banner
        // listens for "job-questions--autosave:conflict".
        this.dispatch("conflict", { detail: body, target: this.element });
        return;
      }

      if (!response.ok) {
        this.setIndicator("error");
        return;
      }

      const body = await response.json();
      this.lockVersionValue = body.lock_version;
      if (this.hasLockVersionTarget) {
        this.lockVersionTarget.value = body.lock_version;
      }
      if (body.card_html) {
        this.replaceLeftPaneCard(body.id, body.card_html);
      }
      this.setIndicator("saved", body.saved_at);
    } catch (e) {
      this.setIndicator("error");
    }
  }

  // Swap the left-pane card for this question so the category badge / draft
  // marker reflect what the responder just saved. No-op if the card isn't
  // currently in the DOM (e.g. the user filtered to a status that excludes
  // it).
  replaceLeftPaneCard(id, html) {
    const card = document.querySelector(`li.jq-v2-question-card[data-question-id="${id}"]`);
    if (!card) return;
    const wasSelected = card.classList.contains("is-selected");
    const tmp = document.createElement("template");
    tmp.innerHTML = html.trim();
    const fresh = tmp.content.firstElementChild;
    if (!fresh) return;
    if (wasSelected) fresh.classList.add("is-selected");
    card.replaceWith(fresh);
  }

  applyServerVersion(serverPayload) {
    this.lockVersionValue = serverPayload.lock_version;
    if (this.hasLockVersionTarget) {
      this.lockVersionTarget.value = serverPayload.lock_version;
    }
    this.textareaTarget.value = serverPayload.server_draft_response || serverPayload.server_response || "";
    this.dirty = false;
    this.setIndicator("idle");
  }

  acceptServerLockKeepLocalText(serverPayload) {
    this.lockVersionValue = serverPayload.lock_version;
    if (this.hasLockVersionTarget) {
      this.lockVersionTarget.value = serverPayload.lock_version;
    }
    this.dirty = true;
    clearTimeout(this.timeout);
    this.save();
  }

  setIndicator(state, savedAt = null) {
    if (!this.hasIndicatorTarget) return;
    const cfg = INDICATOR_STATES[state];
    if (!cfg) return;

    if (cfg.text !== undefined) {
      this.indicatorTarget.textContent = cfg.text;
    } else {
      const label = t(cfg.i18n, cfg.fallback);
      this.indicatorTarget.textContent = `${cfg.prefix || ""}${label}`;
    }

    this.indicatorTarget.dataset.state = state;
    if (state === "saved") {
      this.indicatorTarget.dataset.savedAt = savedAt || new Date().toISOString();
    }
  }

  csrfToken() {
    const tag = document.head.querySelector('meta[name="csrf-token"]');
    return tag ? tag.getAttribute("content") : "";
  }
}
